import { useSWR } from '@tiger/swr';
import lodash from 'lodash';
import type { CountryData, CountryItem } from 'types/common';
import API from 'utils/API';
import http from 'utils/http';
import { getLocation } from 'utils/withLocation';

/**
 * @description 获取国家列表，主要是CountrySelect组件使用
 * @param url 不同配置对应请求的URL
 * @param needLocation 是否需要ip定位国家
 * @param extraCountryItems 额外的国家项
 */
export function useGetCountryList(url: string, needLocation?: boolean, extraCountryItems?: CountryItem[]) {
    const getCountryData = () =>
        http
            .get(url, {
                params: {
                    need_group: 1
                }
            })
            .then(res => {
                const sortedData: CountryData[] = lodash.sortBy(res.data || [], 'letter');
                let finalExtraCountryItems = extraCountryItems || []; // 最终需要添加的额外的国家项

                if (finalExtraCountryItems.length > 0 && sortedData.length > 0) {
                    for (const item of sortedData) {
                        finalExtraCountryItems = finalExtraCountryItems.reduce((prev, cur) => {
                            // 如果额外添加的国家与已有国家重复就不添加，过滤掉这项
                            if (item.items.some(country => country.country_code === cur.country_code)) {
                                return prev;
                            }

                            return [...prev, cur];
                        }, []);

                        if (finalExtraCountryItems.length === 0) {
                            break;
                        }
                    }

                    sortedData[0].items = lodash.uniqBy(
                        sortedData[0].items.concat(finalExtraCountryItems),
                        'country_code'
                    );
                }

                return sortedData;
            });

    return useSWR<{ countryData: CountryData[]; locationCountry?: string }>(
        {
            url,
            params: {
                needLocation,
                extraCountryItems
            }
        },
        {
            fetcher: async () => {
                const [countryData, locationData] = await Promise.all([
                    getCountryData(),
                    needLocation ? getLocation() : undefined
                ]);

                return {
                    countryData,
                    locationCountry: locationData?.location_config?.location
                };
            },
            immutable: true
        }
    );
}

/** 获取全量国家列表 */
export function useGetAllCountry() {
    return useSWR<CountryItem[]>(API.INFOBASE.getAllCountry(), {
        dataChain: res => res.data || [],
        immutable: true
    });
}

/**
 * @description 获取省份列表，目前支持加拿大
 */
export const useProvinceList = (country: string | undefined) => {
    return useSWR<string[]>(
        country === 'CAN'
            ? {
                  url: API.INFOBASE.getEnumConfig(),
                  params: {
                      model: 'ADDRESS',
                      // 传COMMON即可
                      license: 'COMMON',
                      key: 'can_provinces'
                  }
              }
            : null,
        {
            dataChain: res => res.data,
            immutable: true
        }
    );
};
